import React, { Component } from 'react';
import _ from 'underscore';
import AnimateHeight from 'react-animate-height';
import CreatableSelect from 'react-select/lib/Creatable';
import Select from 'react-select';
import * as uuid from 'uuid';
import SelectOptions from './SelectOptions.jsx';
import PropertyWarning from './PropertyWarning.jsx';

class OctetReplacementField extends Component {
  constructor(props) {
    super(props);

    this.radioName = uuid.v4();
    this.animationDuration = process.env.NODE_ENV === 'test' ? 0 : 500;
    this.state = this.parseState(props);
    this.octetOptions = ['na', '1', '2', '3', '4'];
  }

  parseState(props, prevState = {}) {
    if (props.isDHCP) {
      return { octet: 'na', octetReplacement: '', height: 0 };
    }
    let error = this.parseError(props.error);

    return {
      octet: props.metadata.octet || 'na',
      octetReplacement: props.metadata.octetReplacement || '',
      error: error,
      height: this.retrieveHeight(prevState, props, error),
    };
  }

  anySubfieldValuePresent(props) {
    const { metadata, appendValue, canCreateOption } = props;

    return (
      (metadata.octet && metadata.octet !== 'na') ||
      metadata.octetReplacement ||
      (appendValue.value && canCreateOption) ||
      this.isPropertyReference(props)
    );
  }

  retrieveHeight({ height }, props, error) {
    let isAuto =
      this.anySubfieldValuePresent(props) ||
      height ||
      Object.keys(error).length > 0;

    return isAuto ? 'auto' : 0; // show octet field if any value present or octet was expanded or has errors
  }

  parseError(error) {
    let errorObj = {};

    if (typeof error === 'undefined') {
      return errorObj;
    }
    this.props.errorPrefixes.map((prefix) => {
      if (error.indexOf(`${prefix}:`) === 0) {
        errorObj[prefix] = error.replace(`${prefix}:`, '');
      }
    });

    return errorObj;
  }

  errorFor(field) {
    if (
      _.isEmpty(this.state.error) ||
      !Object.prototype.hasOwnProperty.call(this.state.error, field)
    ) {
      return false;
    }
    return this.state.error[field];
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps !== this.props) {
      this.setState(this.parseState(this.props, prevState));
    }
  }

  /*
   * Accepts an object type option that can either the an option from Select or Props.
   * Returns true if the current append value is a property reference
   */
  isPropertyReference(option) {
    if (typeof option !== 'object') {
      return false;
    }
    if (Object.prototype.hasOwnProperty.call(option, 'appendValue')) {
      return (
        option.appendValue.value &&
        option.appendValue.reference &&
        isNaN(parseInt(option.appendValue.value)) &&
        !isNaN(parseInt(option.appendValue.reference))
      );
    }
    return Object.prototype.hasOwnProperty.call(option, 'templateName');
  }

  findSelectedOption(options) {
    const { appendValue, isDHCP } = this.props;

    if (!appendValue.value || isDHCP) {
      return;
    }
    return options.find((opt) => {
      if (this.isPropertyReference(opt)) {
        return (
          opt.templateName == appendValue.value &&
          opt.value == appendValue.reference
        );
      }
      return opt.value == appendValue.value && !appendValue.reference;
    });
  }

  isAppendValueEmpty(props) {
    return (
      !Object.prototype.hasOwnProperty.call(props, 'appendValue') ||
      props.appendValue.value.toString().length == 0
    );
  }

  /* Return shallow copy of Append options for React Select. Add current append if canCreateOption and it's not a reference */
  buildOptionsForAppendSelect(props) {
    let optionsForAppendSelect = props.optionsForAppendSelect.slice();

    /* add current append value to Select options if canCreateOption and append value is not a property reference */
    if (props.canCreateOption && !this.isPropertyReference(props)) {
      optionsForAppendSelect.push({
        value: props.appendValue.value,
        label: props.appendValue.value,
      });
    }
    return optionsForAppendSelect;
  }

  shouldDisableOctetReplacement(octet = this.state.octet) {
    let octetOptions = [...this.octetOptions];
    /** Remove first item ('na' value) from octetOptions */
    octetOptions.shift();
    return !octetOptions.includes(octet);
  }

  handleChangeInOctet(changedOctet) {
    let { octet, octetReplacement } = this.state,
      newOctet = { ...{ octet, octetReplacement }, ...changedOctet };

    if (this.shouldDisableOctetReplacement(changedOctet.octet)) {
      newOctet.octetReplacement = '';
    }

    if (typeof this.props.onOctetChange === 'function') {
      this.props.onOctetChange(newOctet);
    }
  }

  toggle() {
    if (this.props.isDHCP) {
      return;
    }
    this.setState((prevState) => ({
      height: prevState.height === 0 ? 'auto' : 0,
    }));
  }

  showOctetField(octetValue) {
    return this.props.value && !_.isEmpty(octetValue) && octetValue !== 'na';
  }

  shouldDisplayNodeGroupWarning(selectedOption) {
    return (
      controller === 'node_groups' &&
      this.isPropertyReference(selectedOption) &&
      selectedOption.customLabel === 'Node'
    );
  }

  renderNodeGroupWarning() {
    return (
      <small className='ui warning message info reference-warning'>
        Node Group properties referencing Node properties will only be exported
        at the Node level.
      </small>
    );
  }

  handleChangeInAppendOption(e) {
    let newAppendValue = { value: '', reference: '' };

    if (e) {
      if (this.isPropertyReference(e)) {
        newAppendValue = { value: e.templateName, reference: e.id || e.value };
      } else {
        newAppendValue.value = e.value;
      }
    }
    this.props.onAppendChange(newAppendValue);
  }

  createOctetOptions() {
    let isChecked = (option) => option == this.state.octet;

    return this.octetOptions.map((label) => (
      <div
        key={label}
        className='ui radio checkbox'
      >
        <input
          checked={isChecked(label)}
          name={this.radioName}
          type='radio'
          value={label}
          onChange={(e) => this.handleChangeInOctet({ octet: e.target.value })}
        />
        <label>{label.toUpperCase()}</label>
      </div>
    ));
  }

  renderError(field) {
    const error = this.errorFor(field);

    if (!error) {
      return;
    }
    return <div className='error'>{error}</div>;
  }

  shouldDisableSelect(props, selectedOption) {
    return (
      !this.isAppendValueEmpty(props) &&
      !this.isPropertyReference(props) &&
      selectedOption === undefined
    );
  }

  render() {
    const SelectComponent = this.props.canCreateOption
      ? CreatableSelect
      : Select;
    let optionsForAppendSelect = this.buildOptionsForAppendSelect(this.props),
      selectedOption = this.findSelectedOption(optionsForAppendSelect);

    return (
      <div className='OctetReplacement'>
        <div className='ipFieldToogleRow'>
          <a
            className={`ipFieldAdvancedToogle ${this.props.isDHCP ? 'disabled' : ''}`}
            onClick={this.toggle.bind(this)}
          >
            <i
              className={
                this.state.height === 0
                  ? 'icon chevron down'
                  : 'icon chevron up'
              }
            ></i>
            Octet Replacement Settings
          </a>
        </div>
        <AnimateHeight
          duration={this.animationDuration}
          height={this.state.height}
        >
          <div className='ipFieldItemAdvancedSettings'>
            <div className='ipFieldReplace'>
              <div>
                <input
                  readOnly
                  name={
                    this.showOctetField(this.state.octet)
                      ? `${this.props.name}[octet]`
                      : ''
                  }
                  type='hidden'
                  value={this.state.octet}
                />
                <label className='replaceLabel'>Replace Octet:</label>
                {this.createOctetOptions()}
                {this.renderError('octet')}
              </div>
              <div>
                <label>With:</label>
                <input
                  className={`withField ${this.errorFor('octet_replacement') ? 'error' : ''}`}
                  disabled={this.shouldDisableOctetReplacement()}
                  name={
                    this.showOctetField(this.state.octetReplacement)
                      ? `${this.props.name}[octet_replacement]`
                      : ''
                  }
                  type='number'
                  value={this.state.octetReplacement}
                  onChange={(e) =>
                    this.handleChangeInOctet({
                      octetReplacement: e.target.value,
                    })
                  }
                />
                {this.renderError('octet_replacement')}
              </div>
              <div className='appendRow'>
                <If condition={this.props.value}>
                  <input
                    readOnly
                    name={
                      this.props.appendValue.value
                        ? `${this.props.name}[append]`
                        : ''
                    }
                    type='hidden'
                    value={this.props.appendValue.value || ''}
                  />
                  <input
                    readOnly
                    name={
                      this.isPropertyReference(this.props)
                        ? `${this.props.name}[append_reference]`
                        : ''
                    }
                    type='hidden'
                    value={this.props.appendValue.reference || ''}
                  />
                </If>
                <label>Append: /</label>
                <SelectComponent
                  isClearable
                  className={`ipFieldAppendSelect ReactSelect ${this.errorFor('append') ? 'error' : ''}`}
                  disabled={this.shouldDisableSelect(
                    this.props,
                    selectedOption,
                  )}
                  optionRenderer={SelectOptions}
                  options={optionsForAppendSelect}
                  value={selectedOption}
                  onChange={this.handleChangeInAppendOption.bind(this)}
                />
                <label>to value</label>
                {this.renderError('append')}

                <If
                  condition={this.shouldDisplayNodeGroupWarning(selectedOption)}
                >
                  {this.renderNodeGroupWarning()}
                </If>
                <If
                  condition={
                    this.props.checkStatusOfNetmaskProperties &&
                    this.isPropertyReference(this.props)
                  }
                >
                  <PropertyWarning
                    propertyInfo={this.props.statusesOfNetmaskProperties}
                    propertyOptions={optionsForAppendSelect}
                    referenceID={this.props.appendValue.reference}
                    referenceLabel={(selectedOption || {}).label}
                  />
                </If>
              </div>
            </div>
          </div>
        </AnimateHeight>
      </div>
    );
  }
}

module.exports = OctetReplacementField;
